import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { HardHat, MapPin, CircleCheck, CircleDashed, CircleDot, Calendar, ImageOff, FileText, Printer, X, CreditCard, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS = {
  planning: { es: "Planificación", en: "Planning", c: "bg-slate-100 text-slate-600" },
  in_progress: { es: "En progreso", en: "In progress", c: "bg-primary/10 text-primary" },
  completed: { es: "Completado", en: "Completed", c: "bg-accent/10 text-accent" },
};
const PHASES = [
  { key: "before", es: "Antes", en: "Before", icon: CircleDashed, c: "text-slate-500" },
  { key: "during", es: "Durante", en: "During", icon: CircleDot, c: "text-primary" },
  { key: "after", es: "Después", en: "After", icon: CircleCheck, c: "text-accent" },
];

export default function ProjectProgress() {
  const { token } = useParams();
  const { lang, t, toggle } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [paying, setPaying] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [sendingReview, setSendingReview] = useState(false);

  const submitReview = async () => {
    if (!rating) { toast.error(lang === "es" ? "Elige una calificación" : "Pick a rating"); return; }
    setSendingReview(true);
    try {
      await api.post(`/public/projects/${token}/review`, { rating, comment });
      setReviewed(true);
      toast.success(lang === "es" ? "¡Gracias por tu reseña!" : "Thanks for your review!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingReview(false); }
  };

  const payInvoice = async (inv) => {
    setPaying(true);
    try {
      const { data: res } = await api.post(`/public/projects/${token}/invoices/${inv.id}/pay`, { origin_url: window.location.origin });
      if (res.already_paid) { toast.success(lang === "es" ? "Factura ya pagada" : "Invoice already paid"); setPaying(false); return; }
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(lang === "es" ? "No se pudo iniciar el pago" : "Could not start payment"); setPaying(false); }
  };

  useEffect(() => {
    api.get(`/public/projects/${token}`).then((r) => setData(r.data)).catch(() => setErr(true));
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("paid");
    if (sid) {
      api.get(`/public/invoices/status/${sid}`).then((r) => {
        if (r.data.payment_status === "paid") {
          toast.success(lang === "es" ? "¡Pago recibido, gracias!" : "Payment received, thank you!");
          api.get(`/public/projects/${token}`).then((rr) => setData(rr.data)).catch(() => {});
        }
        window.history.replaceState({}, "", `/p/${token}`);
      }).catch(() => {});
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmWork = async () => {
    setConfirming(true);
    try {
      const { data: res } = await api.post(`/public/projects/${token}/confirm`);
      setData((d) => ({ ...d, client_confirmed: true, client_confirmed_at: res.client_confirmed_at }));
    } catch (e) { /* noop */ }
    finally { setConfirming(false); }
  };

  if (err) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100 px-6 text-center">
        <ImageOff className="h-12 w-12 text-slate-300" />
        <h1 className="mt-4 font-head text-2xl font-extrabold text-secondary">{lang === "es" ? "Enlace no disponible" : "Link unavailable"}</h1>
        <p className="mt-1 text-slate-500">{lang === "es" ? "Este seguimiento de proyecto ya no está disponible." : "This project tracking is no longer available."}</p>
        <Link to="/" className="mt-6 rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-600">{t("brand")}</Link>
      </div>
    );
  }
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-400">…</div>;

  const st = STATUS[data.project.status];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="project-progress-page">
      <header className="border-b border-slate-800 bg-secondary">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            {data.business.logo ? <img src={data.business.logo} alt="logo" className="h-10 w-10 rounded-sm bg-white object-contain p-0.5" /> : <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-5 w-5" /></span>}
            <div>
              <p className="font-head text-lg font-extrabold text-white">{data.business.name}</p>
              {(data.business.city || data.business.state) && <p className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{data.business.city}{data.business.state ? `, ${data.business.state}` : ""}</p>}
            </div>
          </div>
          <button onClick={toggle} className="rounded-sm border border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800" data-testid="progress-lang-toggle">{lang === "es" ? "EN" : "ES"}</button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="rounded-sm border border-slate-200 bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{data.project.title}</h1>
            {st && <span className={`rounded-sm px-3 py-1 text-xs font-bold uppercase ${st.c}`} data-testid="progress-status">{lang === "es" ? st.es : st.en}</span>}
          </div>
          {data.project.description && <p className="mt-2 text-sm text-slate-600">{data.project.description}</p>}
          {(data.project.start_date || data.project.end_date) && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-slate-400"><Calendar className="h-4 w-4" />{data.project.start_date || "?"} → {data.project.end_date || "?"}</p>
          )}
        </div>

        {data.client_confirmed ? (
          <div className="mt-6 flex items-center gap-3 rounded-sm border border-accent/30 bg-accent/5 p-4" data-testid="progress-confirmed">
            <CircleCheck className="h-6 w-6 text-accent" />
            <div>
              <p className="font-semibold text-secondary">{lang === "es" ? "¡Trabajo confirmado como recibido!" : "Work confirmed as received!"}</p>
              {data.client_confirmed_at && <p className="text-xs text-slate-500">{lang === "es" ? "Confirmado el" : "Confirmed on"} {new Date(data.client_confirmed_at).toLocaleDateString()}</p>}
            </div>
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
            <p className="text-sm text-slate-600">{lang === "es" ? "¿El trabajo cumple con lo acordado? Confírmalo para notificar al negocio." : "Is the work as agreed? Confirm to notify the business."}</p>
            <Button onClick={confirmWork} disabled={confirming} className="rounded-sm bg-accent hover:bg-emerald-600" data-testid="progress-confirm-btn"><CircleCheck className="mr-2 h-4 w-4" />{confirming ? (lang === "es" ? "Enviando…" : "Sending…") : (lang === "es" ? "Confirmar trabajo recibido" : "Confirm work received")}</Button>
          </div>
        )}

        {data.client_confirmed && (
          (data.client_reviewed || reviewed) ? (
            <div className="mt-4 flex items-center gap-3 rounded-sm border border-primary/30 bg-primary/5 p-4" data-testid="review-done">
              <Star className="h-6 w-6 fill-primary text-primary" />
              <p className="font-semibold text-secondary">{lang === "es" ? "¡Gracias por tu reseña!" : "Thanks for your review!"}</p>
            </div>
          ) : (
            <div className="mt-4 rounded-sm border border-slate-200 bg-white p-5" data-testid="project-review-card">
              <p className="font-head font-bold text-secondary">{lang === "es" ? "¿Cómo fue tu experiencia?" : "How was your experience?"}</p>
              <p className="mt-0.5 text-sm text-slate-500">{lang === "es" ? "Tu reseña ayuda a otros clientes a confiar en este profesional." : "Your review helps other customers trust this pro."}</p>
              <div className="mt-3 flex gap-0.5" onMouseLeave={() => setHoverStar(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onMouseEnter={() => setHoverStar(n)} onClick={() => setRating(n)} data-testid={`prj-star-${n}`}>
                    <Star className={`h-8 w-8 ${n <= (hoverStar || rating) ? "fill-primary text-primary" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder={lang === "es" ? "Cuéntanos tu experiencia (opcional)" : "Tell us about your experience (optional)"} className="mt-3 w-full resize-y rounded-sm border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" data-testid="prj-review-comment" />
              <Button onClick={submitReview} disabled={sendingReview} className="mt-3 rounded-sm bg-primary hover:bg-orange-600" data-testid="project-review-submit">{sendingReview ? "…" : (lang === "es" ? "Publicar reseña" : "Post review")}</Button>
            </div>
          )
        )}

        <h2 className="mt-8 font-head text-lg font-bold text-secondary">{lang === "es" ? "Avance del trabajo" : "Work progress"}</h2>
        {data.updates.length === 0 ? (
          <p className="mt-4 rounded-sm border border-dashed border-slate-300 bg-white py-12 text-center text-slate-500">{lang === "es" ? "Aún no hay actualizaciones. Vuelve pronto." : "No updates yet. Check back soon."}</p>
        ) : (
          <div className="mt-4 space-y-4">
            {data.updates.map((u) => {
              const ph = PHASES.find((p) => p.key === u.phase) || PHASES[1];
              const Icon = ph.icon;
              return (
                <div key={u.id} className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`progress-update-${u.id}`}>
                  <div className="flex items-center justify-between">
                    <span className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide ${ph.c}`}><Icon className="h-4 w-4" />{lang === "es" ? ph.es : ph.en}</span>
                    <span className="text-xs text-slate-400">{new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                  {u.note && <p className="mt-2 text-sm text-slate-700">{u.note}</p>}
                  {u.photos?.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {u.photos.map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noreferrer" className="overflow-hidden rounded-sm border border-slate-200">
                          <img src={p} alt="avance" className="h-40 w-full object-cover transition-transform hover:scale-105" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {data.invoices?.length > 0 && (
          <div className="mt-6 rounded-sm border border-slate-200 bg-white p-5" data-testid="progress-invoices">
            <h2 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "Facturas" : "Invoices"}</h2>
            <div className="mt-3 divide-y divide-slate-100">
              {data.invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between py-2.5" data-testid={`progress-invoice-${inv.id}`}>
                  <div>
                    <span className="font-semibold text-secondary">{inv.number}</span>
                    <span className={`ml-2 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${inv.status === "paid" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>{inv.status === "paid" ? (lang === "es" ? "Pagada" : "Paid") : (lang === "es" ? "Pendiente" : "Pending")}</span>
                    <span className="ml-2 font-bold text-secondary">${inv.total.toFixed(2)}</span>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setInvoice(inv)} data-testid={`progress-invoice-view-${inv.id}`}><FileText className="mr-1 h-3.5 w-3.5" />{lang === "es" ? "Descargar" : "Download"}</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 text-center text-xs text-slate-400">{lang === "es" ? "Seguimiento generado por" : "Tracking powered by"} <Link to="/" className="font-semibold text-primary">{t("brand")}</Link></p>
      </main>

      {invoice && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="progress-invoice-modal" onClick={() => setInvoice(null)}>
          <div className="invoice-pub-print mt-8 w-full max-w-md rounded-sm bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="no-print flex justify-end"><button onClick={() => setInvoice(null)} className="text-slate-400 hover:text-secondary"><X className="h-5 w-5" /></button></div>
            <div className="flex items-center gap-3 border-b border-slate-200 pb-4">
              {data.business.logo ? <img src={data.business.logo} alt="logo" className="h-12 w-12 object-contain" /> : <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary text-white"><HardHat className="h-6 w-6" /></span>}
              <div><p className="font-head text-lg font-bold text-secondary">{data.business.name}</p><p className="text-xs text-slate-400">{invoice.number}</p></div>
            </div>
            <table className="mt-4 w-full text-sm">
              <tbody>
                {invoice.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="py-2 text-slate-600">{it.description} × {it.quantity}</td><td className="py-2 text-right font-semibold">${(it.quantity * it.unit_price).toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>${invoice.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-slate-500"><span>{lang === "es" ? "Impuesto" : "Tax"} ({invoice.tax_rate}%)</span><span>${invoice.tax.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-2 font-head text-lg font-bold text-secondary"><span>Total</span><span>${invoice.total.toFixed(2)}</span></div>
            </div>
            <Button onClick={() => window.print()} className="no-print mt-6 w-full rounded-sm bg-secondary hover:bg-slate-700" data-testid="progress-invoice-print"><Printer className="mr-2 h-4 w-4" />{lang === "es" ? "Imprimir / PDF" : "Print / PDF"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
